import { useState, useEffect, useCallback } from "react";
import {
  Phone,
  PhoneCall,
  PhoneOff,
  Loader2,
  Copy,
  Check,
  Settings,
  BarChart3,
  AlertCircle,
  CheckCircle,
  Globe,
  MessageSquare,
  PhoneForwarded,
  FileText,
  MapPin,
  Link2,
  Clock,
  Users,
  TrendingUp,
  Calendar,
  Sparkles,
  History,
  ChevronDown,
  Truck,
  ShoppingBag,
  Crown,
  RefreshCw,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLocation } from "../contexts/LocationContext";
import { useAuth } from "../contexts/AuthContext";
import { useConfirm } from "../contexts/ConfirmContext";
import { usePermissions } from "../hooks/usePermissions";
import apiClient from "../lib/api-client";
import VoiceAiFaqSection from "../components/VoiceAiFaqSection";

interface VoiceAiStatus {
  enabled: boolean;
  assistantId: string | null;
  phoneNumberId: string | null;
  phoneNumber: string | null;
  assistantActive: boolean;
  assistantError?: string;
  settings: {
    greeting: string | null;
    language: string | null;
    restaurantInfo: string | null;
    events: string | null;
    transferPhone: string | null;
    menuUrl: string | null;
    mapUrl: string | null;
    webUrl: string | null;
    deliveryEnabled: boolean;
    pickupEnabled: boolean;
    phoneOrdersEnabled?: boolean;
    glovoUrl: string | null;
    uberEatsUrl: string | null;
    justEatUrl: string | null;
    pickupUrl: string | null;
  };
}

interface VoiceAiStats {
  totalCalls: number;
  totalMinutes: number;
  estimatedCost: number;
  byIntent: Record<string, number>;
  byOutcome: Record<string, number>;
  monthly: Array<{
    month: string;
    calls: number;
    minutes: number;
  }>;
}

interface VoiceAiCall {
  id: string;
  callerPhone: string | null;
  customerName: string | null;
  duration: number | null;
  intent: string | null;
  outcome: string | null;
  reservationId: string | null;
  transcript: string | null;
  endedReason?: string | null;
  recordingUrl?: string | null;
  summary?: string | null;
  successEvaluation?: string | null;
  cost?: number | null;
  failureReason?: string | null;
  createdAt: string;
}

type Tab = "status" | "settings" | "stats" | "history";

export default function VoiceAI() {
  const { currentLocation, currentLocationId } = useLocation();
  const { user } = useAuth();
  const { confirm } = useConfirm();
  const { can } = usePermissions();
  // Coste = dato de plataforma: SOLO owner (no gestionable por permisos).
  const isOwner = user?.role === "owner";

  const [activeTab, setActiveTab] = useState<Tab>("status");
  const [status, setStatus] = useState<VoiceAiStatus | null>(null);
  const [stats, setStats] = useState<VoiceAiStats | null>(null);
  const [calls, setCalls] = useState<VoiceAiCall[]>([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [callsPage, setCallsPage] = useState(1);
  const [callsTotalPages, setCallsTotalPages] = useState(0);
  const [callsFilter, setCallsFilter] = useState<string>("");
  const [callsSearch, setCallsSearch] = useState<string>("");
  const [callsEndedReason, setCallsEndedReason] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [enabling, setEnabling] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [syncingAssistant, setSyncingAssistant] = useState(false);
  const [copied, setCopied] = useState(false);
  const [needsUpgrade, setNeedsUpgrade] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const navigate = useNavigate();

  // Settings form
  const [greeting, setGreeting] = useState("");
  const [language, setLanguage] = useState("es");
  const [restaurantInfo, setRestaurantInfo] = useState("");
  const [events, setEvents] = useState("");
  const [transferPhone, setTransferPhone] = useState("");
  const [menuUrl, setMenuUrl] = useState("");
  const [mapUrl, setMapUrl] = useState("");
  const [webUrl, setWebUrl] = useState("");
  const [deliveryEnabled, setDeliveryEnabled] = useState(false);
  const [pickupEnabled, setPickupEnabled] = useState(false);
  const [phoneOrdersEnabled, setPhoneOrdersEnabled] = useState(true);
  const [glovoUrl, setGlovoUrl] = useState("");
  const [uberEatsUrl, setUberEatsUrl] = useState("");
  const [justEatUrl, setJustEatUrl] = useState("");
  const [pickupUrl, setPickupUrl] = useState("");

  const fetchStatus = useCallback(async () => {
    if (!currentLocationId) return;
    try {
      const res = await apiClient.get(
        `/vapi/status?locationId=${currentLocationId}`,
      );
      const data = res.data.data as VoiceAiStatus;
      setStatus(data);
      // Populate settings form
      setGreeting(data.settings.greeting || "");
      setLanguage(data.settings.language || "es");
      setRestaurantInfo(data.settings.restaurantInfo || "");
      setEvents(data.settings.events || "");
      setTransferPhone(data.settings.transferPhone || "");
      setMenuUrl(data.settings.menuUrl || "");
      setMapUrl(data.settings.mapUrl || "");
      setWebUrl(data.settings.webUrl || "");
      setDeliveryEnabled(data.settings.deliveryEnabled || false);
      setPickupEnabled(data.settings.pickupEnabled || false);
      setPhoneOrdersEnabled(data.settings.phoneOrdersEnabled ?? true);
      setGlovoUrl(data.settings.glovoUrl || "");
      setUberEatsUrl(data.settings.uberEatsUrl || "");
      setJustEatUrl(data.settings.justEatUrl || "");
      setPickupUrl(data.settings.pickupUrl || "");
    } catch {
      // Voice AI not available for this location
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [currentLocationId]);

  const fetchStats = useCallback(async () => {
    if (!currentLocationId) return;
    try {
      const res = await apiClient.get(
        `/vapi/stats?locationId=${currentLocationId}`,
      );
      setStats(res.data.data);
    } catch {
      setStats(null);
    }
  }, [currentLocationId]);

  const fetchCalls = useCallback(
    async (page: number = 1) => {
      if (!currentLocationId) return;
      setCallsLoading(true);
      try {
        const params = new URLSearchParams({
          locationId: currentLocationId,
          page: page.toString(),
          perPage: "15",
        });
        if (callsFilter) params.append("outcome", callsFilter);
        if (callsEndedReason) params.append("endedReason", callsEndedReason);
        if (callsSearch) params.append("search", callsSearch);

        const res = await apiClient.get(`/vapi/calls?${params}`);
        setCalls(res.data.data.calls);
        setCallsTotalPages(res.data.data.pagination.totalPages);
        setCallsPage(page);
      } catch {
        setCalls([]);
      } finally {
        setCallsLoading(false);
      }
    },
    [currentLocationId, callsFilter, callsEndedReason, callsSearch],
  );

  useEffect(() => {
    setLoading(true);
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (activeTab === "stats") {
      fetchStats();
    } else if (activeTab === "history") {
      fetchCalls(1);
    }
  }, [activeTab, fetchStats, fetchCalls]);

  const handleEnable = async () => {
    if (!currentLocationId) return;
    setEnabling(true);
    setMessage(null);
    try {
      const res = await apiClient.post("/vapi/enable", {
        locationId: currentLocationId,
      });
      setMessage({ type: "success", text: res.data.message });
      await fetchStatus();
    } catch (err: any) {
      const status = err.response?.status;
      const msg =
        err.response?.data?.message || "Error activando la centralita de IA";
      if (status === 403) {
        setNeedsUpgrade(true);
        setUpgradeMessage(msg);
      } else {
        setMessage({ type: "error", text: msg });
      }
    } finally {
      setEnabling(false);
    }
  };

  const handleDisable = async () => {
    if (!currentLocationId) return;
    if (
      !(await confirm({
        title: "¿Desactivar la centralita de IA?",
        message: "Se liberará el número de teléfono asignado.",
        confirmText: "Sí, desactivar",
        danger: true,
      }))
    )
      return;
    setDisabling(true);
    setMessage(null);
    try {
      const res = await apiClient.post("/vapi/disable", {
        locationId: currentLocationId,
      });
      setMessage({ type: "success", text: res.data.message });
      await fetchStatus();
    } catch (err: any) {
      setMessage({
        type: "error",
        text:
          err.response?.data?.message ||
          "Error desactivando la centralita de IA",
      });
    } finally {
      setDisabling(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!currentLocationId) return;
    setSavingSettings(true);
    setMessage(null);
    try {
      const res = await apiClient.patch("/vapi/settings", {
        locationId: currentLocationId,
        greeting: greeting || null,
        language,
        restaurantInfo: restaurantInfo || null,
        events: events || null,
        transferPhone: transferPhone || null,
        menuUrl: menuUrl || null,
        mapUrl: mapUrl || null,
        webUrl: webUrl || null,
        deliveryEnabled,
        pickupEnabled,
        phoneOrdersEnabled,
        glovoUrl: glovoUrl || null,
        uberEatsUrl: uberEatsUrl || null,
        justEatUrl: justEatUrl || null,
        pickupUrl: pickupUrl || null,
      });
      setMessage({ type: "success", text: res.data.message });
    } catch (err: any) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Error guardando la configuración",
      });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSyncAssistant = async () => {
    if (!currentLocationId) return;
    setSyncingAssistant(true);
    setMessage(null);
    try {
      const res = await apiClient.post("/vapi/sync-assistant", {
        locationId: currentLocationId,
      });
      setMessage({ type: "success", text: res.data.message });
    } catch (err: any) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Error al sincronizar con Carmen",
      });
    } finally {
      setSyncingAssistant(false);
    }
  };

  const copyPhone = async () => {
    if (!status?.phoneNumber) return;
    await navigator.clipboard.writeText(status.phoneNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const tabs: { id: Tab; label: string; icon: React.ElementType }[] = [
    { id: "status", label: "Estado", icon: Phone },
    { id: "settings", label: "Configuración", icon: Settings },
    { id: "stats", label: "Estadísticas", icon: BarChart3 },
    { id: "history", label: "Historial", icon: History },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-0 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-900">
          Centralita IA
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Asistente telefónico con inteligencia artificial para{" "}
          <span className="font-medium">{currentLocation?.name}</span>
        </p>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`mb-4 p-3 rounded-lg flex items-center gap-2 text-sm ${
            message.type === "success"
              ? "bg-green-50 text-green-700 border border-green-200"
              : "bg-red-50 text-red-700 border border-red-200"
          }`}
        >
          {message.type === "success" ? (
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
          )}
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-white rounded-lg p-1 shadow-sm border border-gray-200 w-fit max-w-full overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            disabled={tab.id !== "status" && !status?.enabled}
            className={`flex items-center gap-1.5 md:gap-2 flex-shrink-0 whitespace-nowrap px-3 md:px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? "bg-primary-100 text-primary-700"
                : tab.id !== "status" && !status?.enabled
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "status" && (
        <StatusTab
          status={status}
          enabling={enabling}
          disabling={disabling}
          copied={copied}
          needsUpgrade={needsUpgrade}
          upgradeMessage={upgradeMessage}
          syncing={syncingAssistant}
          onEnable={handleEnable}
          onDisable={handleDisable}
          onCopy={copyPhone}
          onSync={handleSyncAssistant}
          onNavigateUpgrade={() => navigate("/subscription")}
        />
      )}

      {activeTab === "settings" && status?.enabled && (
        <SettingsTab
          greeting={greeting}
          language={language}
          restaurantInfo={restaurantInfo}
          events={events}
          transferPhone={transferPhone}
          menuUrl={menuUrl}
          mapUrl={mapUrl}
          webUrl={webUrl}
          saving={savingSettings}
          onGreetingChange={setGreeting}
          onLanguageChange={setLanguage}
          onRestaurantInfoChange={setRestaurantInfo}
          onEventsChange={setEvents}
          onTransferPhoneChange={setTransferPhone}
          onMenuUrlChange={setMenuUrl}
          onMapUrlChange={setMapUrl}
          onWebUrlChange={setWebUrl}
          deliveryEnabled={deliveryEnabled}
          pickupEnabled={pickupEnabled}
          phoneOrdersEnabled={phoneOrdersEnabled}
          onPhoneOrdersEnabledChange={setPhoneOrdersEnabled}
          glovoUrl={glovoUrl}
          uberEatsUrl={uberEatsUrl}
          justEatUrl={justEatUrl}
          pickupUrl={pickupUrl}
          onDeliveryEnabledChange={setDeliveryEnabled}
          onPickupEnabledChange={setPickupEnabled}
          onGlovoUrlChange={setGlovoUrl}
          onUberEatsUrlChange={setUberEatsUrl}
          onJustEatUrlChange={setJustEatUrl}
          onPickupUrlChange={setPickupUrl}
          onSave={handleSaveSettings}
          locationId={currentLocationId ?? ""}
          canSuggest={can("integraciones:gestionar")}
        />
      )}

      {activeTab === "stats" && status?.enabled && (
        <StatsTab stats={stats} isOwner={isOwner} />
      )}

      {activeTab === "history" && status?.enabled && (
        <HistoryTab
          calls={calls}
          loading={callsLoading}
          page={callsPage}
          totalPages={callsTotalPages}
          filter={callsFilter}
          search={callsSearch}
          endedReason={callsEndedReason}
          isOwner={isOwner}
          onFilterChange={(v) => {
            setCallsFilter(v);
            setCallsPage(1);
          }}
          onSearchChange={(v) => {
            setCallsSearch(v);
            setCallsPage(1);
          }}
          onEndedReasonChange={(v) => {
            setCallsEndedReason(v);
            setCallsPage(1);
          }}
          onPageChange={fetchCalls}
        />
      )}
    </div>
  );
}

/* ==================== STATUS TAB ==================== */

function StatusTab({
  status,
  enabling,
  disabling,
  copied,
  needsUpgrade,
  upgradeMessage,
  syncing,
  onEnable,
  onDisable,
  onCopy,
  onSync,
  onNavigateUpgrade,
}: {
  status: VoiceAiStatus | null;
  enabling: boolean;
  disabling: boolean;
  copied: boolean;
  needsUpgrade: boolean;
  upgradeMessage: string;
  syncing: boolean;
  onEnable: () => void;
  onDisable: () => void;
  onCopy: () => void;
  onSync: () => void;
  onNavigateUpgrade: () => void;
}) {
  if (needsUpgrade) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
        <div className="text-center max-w-md mx-auto">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Crown className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Mejora tu plan
          </h2>
          <p className="text-gray-500 text-sm mb-2">{upgradeMessage}</p>
          <p className="text-gray-400 text-xs mb-6">
            La Centralita IA está disponible como add-on en los planes que la
            incluyen. Incluye un número de teléfono español dedicado y un
            asistente de IA que gestiona llamadas, reservas y disponibilidad
            24/7.
          </p>
          <button
            onClick={onNavigateUpgrade}
            className="inline-flex items-center gap-2 px-6 py-3 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors"
          >
            <Crown className="w-5 h-5" />
            Ver planes disponibles
          </button>
        </div>
      </div>
    );
  }

  if (!status || !status.enabled) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
        <div className="text-center max-w-md mx-auto">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Phone className="w-8 h-8 text-primary-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Centralita IA desactivada
          </h2>
          {/* Con voz cancelada pero número conservado (lo sigue usando
              WhatsApp IA), el mensaje genérico de "se asignará un número"
              confunde: se explica que su número sigue siendo suyo. */}
          {status?.phoneNumber ? (
            <p className="text-gray-500 text-sm mb-6">
              La atención de llamadas está desactivada, pero tu número{" "}
              <strong className="text-gray-700">{status.phoneNumber}</strong>{" "}
              se conserva porque WhatsApp IA lo sigue usando. Si reactivas la
              centralita, las llamadas volverán a atenderse en ese mismo
              número — sin cambios para tus clientes.
            </p>
          ) : (
            <p className="text-gray-500 text-sm mb-6">
              Activa la centralita de IA para tu restaurante. Se asignará
              automáticamente un número de teléfono español (+34) y se creará
              un asistente de inteligencia artificial que gestionará las
              llamadas, consultará disponibilidad y creará reservas.
            </p>
          )}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
            <h3 className="text-sm font-medium text-blue-900 mb-2">
              ¿Qué hace la Centralita IA?
            </h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                Atiende llamadas automáticamente 24/7
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                Consulta disponibilidad en tiempo real
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                Crea reservas directamente en el sistema
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                Envía confirmaciones por WhatsApp
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                Transfiere a un humano si es necesario
              </li>
            </ul>
          </div>
          <button
            onClick={onEnable}
            disabled={enabling}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {enabling ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Activando...
              </>
            ) : (
              <>
                <PhoneCall className="w-5 h-5" />
                Activar Centralita IA
              </>
            )}
          </button>
          {enabling && (
            <p className="text-xs text-gray-400 mt-3">
              Comprando número y configurando asistente... esto puede tardar
              unos segundos.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Status Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <PhoneCall className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">
                Centralita IA activa
              </h2>
              <p className="text-sm text-gray-500">
                {status.assistantActive
                  ? "El asistente está funcionando correctamente"
                  : "Verificando estado del asistente..."}
              </p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            Activo
          </span>
        </div>

        {/* Phone Number */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Número de teléfono asignado
          </label>
          <div className="flex items-center gap-3">
            <span className="text-2xl font-bold text-gray-900 tracking-wide">
              {status.phoneNumber}
            </span>
            <button
              onClick={onCopy}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              title="Copiar número"
            >
              {copied ? (
                <Check className="w-4 h-4 text-green-600" />
              ) : (
                <Copy className="w-4 h-4 text-gray-500" />
              )}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Publica este número en tu web, Google, redes sociales y tarjetas de
            visita. Las llamadas serán atendidas automáticamente por la IA.
          </p>
        </div>

        {/* Quick Info */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <Globe className="w-5 h-5 text-blue-600 mx-auto mb-1" />
            <p className="text-xs font-medium text-blue-900">
              {status.settings.language === "es"
                ? "Español"
                : status.settings.language === "en"
                  ? "Inglés"
                  : status.settings.language || "Español"}
            </p>
          </div>
          <div className="bg-purple-50 rounded-lg p-3 text-center">
            <MessageSquare className="w-5 h-5 text-purple-600 mx-auto mb-1" />
            <p className="text-xs font-medium text-purple-900">
              {status.settings.greeting
                ? "Saludo personalizado"
                : "Saludo automático"}
            </p>
          </div>
          <div className="bg-amber-50 rounded-lg p-3 text-center">
            <PhoneForwarded className="w-5 h-5 text-amber-600 mx-auto mb-1" />
            <p className="text-xs font-medium text-amber-900">
              {status.settings.transferPhone
                ? "Transferencia activa"
                : "Sin transferencia"}
            </p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-3 text-center">
            <FileText className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
            <p className="text-xs font-medium text-emerald-900">
              {status.settings.restaurantInfo
                ? "Info configurada"
                : "Sin info extra"}
            </p>
          </div>
        </div>

        {/* Disable Button */}
        <div className="border-t border-gray-200 pt-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <button
            onClick={onSync}
            disabled={syncing || disabling}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50"
            title="Propaga los cambios de horario y configuración a Carmen inmediatamente"
          >
            {syncing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sincronizando...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Sincronizar con Carmen
              </>
            )}
          </button>
          <button
            onClick={onDisable}
            disabled={disabling || syncing}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
          >
            {disabling ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Desactivando...
              </>
            ) : (
              <>
                <PhoneOff className="w-4 h-4" />
                Desactivar Centralita IA
              </>
            )}
          </button>
          <p className="text-xs text-gray-400 mt-1">
            Se liberará el número de teléfono y se eliminará el asistente.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ==================== SETTINGS TAB ==================== */

function SettingsTab({
  greeting,
  language,
  restaurantInfo,
  events,
  transferPhone,
  menuUrl,
  mapUrl,
  webUrl,
  deliveryEnabled,
  pickupEnabled,
  phoneOrdersEnabled,
  onPhoneOrdersEnabledChange,
  glovoUrl,
  uberEatsUrl,
  justEatUrl,
  pickupUrl,
  saving,
  onGreetingChange,
  onLanguageChange,
  onRestaurantInfoChange,
  onEventsChange,
  onTransferPhoneChange,
  onMenuUrlChange,
  onMapUrlChange,
  onWebUrlChange,
  onDeliveryEnabledChange,
  onPickupEnabledChange,
  onGlovoUrlChange,
  onUberEatsUrlChange,
  onJustEatUrlChange,
  onPickupUrlChange,
  onSave,
  locationId,
  canSuggest,
}: {
  greeting: string;
  language: string;
  restaurantInfo: string;
  events: string;
  transferPhone: string;
  menuUrl: string;
  mapUrl: string;
  webUrl: string;
  deliveryEnabled: boolean;
  pickupEnabled: boolean;
  phoneOrdersEnabled: boolean;
  onPhoneOrdersEnabledChange: (v: boolean) => void;
  glovoUrl: string;
  uberEatsUrl: string;
  justEatUrl: string;
  pickupUrl: string;
  saving: boolean;
  onGreetingChange: (v: string) => void;
  onLanguageChange: (v: string) => void;
  onRestaurantInfoChange: (v: string) => void;
  onEventsChange: (v: string) => void;
  onTransferPhoneChange: (v: string) => void;
  onMenuUrlChange: (v: string) => void;
  onMapUrlChange: (v: string) => void;
  onWebUrlChange: (v: string) => void;
  onDeliveryEnabledChange: (v: boolean) => void;
  onPickupEnabledChange: (v: boolean) => void;
  onGlovoUrlChange: (v: string) => void;
  onUberEatsUrlChange: (v: string) => void;
  onJustEatUrlChange: (v: string) => void;
  onPickupUrlChange: (v: string) => void;
  onSave: () => void;
  locationId: string;
  canSuggest: boolean;
}) {
  return (
    <div className="space-y-4">
      {/* General */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-gray-400" />
          General
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Saludo personalizado
            </label>
            <textarea
              value={greeting}
              onChange={(e) => onGreetingChange(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              placeholder="Ej: ¡Hola! Gracias por llamar a Kursaal Lounge. ¿En qué puedo ayudarte?"
            />
            <p className="text-xs text-gray-400 mt-1">
              Si lo dejas vacío, la IA generará un saludo automático con el
              nombre de tu restaurante.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Idioma
            </label>
            <select
              value={language}
              onChange={(e) => onLanguageChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            >
              <option value="es">Español</option>
              <option value="en">Inglés</option>
              <option value="fr">Francés</option>
              <option value="de">Alemán</option>
              <option value="it">Italiano</option>
              <option value="pt">Portugués</option>
            </select>
          </div>
        </div>
      </div>

      {/* Info del restaurante */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-gray-400" />
          Información del restaurante
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Información adicional
            </label>
            <textarea
              value={restaurantInfo}
              onChange={(e) => onRestaurantInfoChange(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              placeholder="Ej: Somos un restaurante especializado en cocina mediterránea. Tenemos terraza. Aceptamos mascotas en la terraza. Parking gratuito disponible."
            />
            <p className="text-xs text-gray-400 mt-1">
              Esta información será usada por la IA para responder preguntas
              sobre tu restaurante.
            </p>
          </div>
        </div>
      </div>

      {/* Preguntas frecuentes */}
      {locationId && (
        <VoiceAiFaqSection locationId={locationId} canSuggest={canSuggest} />
      )}

      {/* Eventos y novedades */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-gray-400" />
          Eventos y novedades
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Próximos eventos o novedades
            </label>
            <textarea
              value={events}
              onChange={(e) => onEventsChange(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              placeholder="Ej: Este viernes 28 de marzo tenemos noche de jazz en vivo a partir de las 21:00. Menú especial de Semana Santa disponible del 14 al 20 de abril."
            />
            <p className="text-xs text-gray-400 mt-1">
              La IA compartirá esta información en el saludo de cada llamada.
              Ten en cuenta que esta información la escucharán todas las
              personas que llamen por teléfono.
            </p>
          </div>
        </div>
      </div>

      {/* Transferencia */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <PhoneForwarded className="w-5 h-5 text-gray-400" />
          Transferencia de llamadas
        </h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Teléfono para transferir
          </label>
          <input
            type="tel"
            value={transferPhone}
            onChange={(e) => onTransferPhoneChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            placeholder="+34 612 345 678"
          />
          <p className="text-xs text-gray-400 mt-1">
            Si el cliente pide hablar con una persona, la IA transferirá la
            llamada a este número.
          </p>
        </div>
      </div>

      {/* URLs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Link2 className="w-5 h-5 text-gray-400" />
          Enlaces útiles
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          La IA puede enviar estos enlaces por WhatsApp cuando el cliente los
          solicite.
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Globe className="w-3.5 h-3.5 inline mr-1" />
              Web del restaurante
            </label>
            <input
              type="url"
              value={webUrl}
              onChange={(e) => onWebUrlChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              placeholder="https://mirestaurante.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <FileText className="w-3.5 h-3.5 inline mr-1" />
              URL de la carta / menú
            </label>
            <input
              type="url"
              value={menuUrl}
              onChange={(e) => onMenuUrlChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              placeholder="https://mirestaurante.com/carta"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <MapPin className="w-3.5 h-3.5 inline mr-1" />
              URL de Google Maps
            </label>
            <input
              type="url"
              value={mapUrl}
              onChange={(e) => onMapUrlChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              placeholder="https://maps.google.com/..."
            />
          </div>
        </div>
      </div>

      {/* Pedidos a domicilio y recoger */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Truck className="w-5 h-5 text-gray-400" />
          Pedidos a domicilio y recoger
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Configura si tu restaurante ofrece servicio a domicilio o para
          recoger. La IA informará a los clientes y les enviará los enlaces por
          WhatsApp.
        </p>
        <div className="space-y-4">
          {/* Delivery toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Truck className="w-4 h-4 text-orange-500" />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Pedidos a domicilio
                </p>
                <p className="text-xs text-gray-500">
                  Glovo, Uber Eats, Just Eat...
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => onDeliveryEnabledChange(!deliveryEnabled)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                deliveryEnabled ? "bg-primary-600" : "bg-gray-200"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  deliveryEnabled ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          </div>

          {deliveryEnabled && (
            <div className="space-y-3 pl-4 border-l-2 border-orange-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL de Glovo
                </label>
                <input
                  type="url"
                  value={glovoUrl}
                  onChange={(e) => onGlovoUrlChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  placeholder="https://glovoapp.com/es/es/..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL de Uber Eats
                </label>
                <input
                  type="url"
                  value={uberEatsUrl}
                  onChange={(e) => onUberEatsUrlChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  placeholder="https://www.ubereats.com/es/store/..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL de Just Eat
                </label>
                <input
                  type="url"
                  value={justEatUrl}
                  onChange={(e) => onJustEatUrlChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  placeholder="https://www.just-eat.es/restaurants/..."
                />
              </div>
              <p className="text-xs text-gray-400">
                Añade al menos un enlace. La IA enviará todos los enlaces
                configurados por WhatsApp al cliente.
              </p>
            </div>
          )}

          {/* Pickup toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <ShoppingBag className="w-4 h-4 text-green-500" />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Pedidos para recoger
                </p>
                <p className="text-xs text-gray-500">
                  La IA gestionará los pedidos según la opción de abajo
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => onPickupEnabledChange(!pickupEnabled)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                pickupEnabled ? "bg-primary-600" : "bg-gray-200"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  pickupEnabled ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          </div>

          {pickupEnabled && (
            <div className="space-y-3 pl-4 border-l-2 border-green-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Enlace para pedir y recoger (tu web)
                </label>
                <input
                  type="url"
                  value={pickupUrl}
                  onChange={(e) => onPickupUrlChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  placeholder="https://turestaurante.com/pedidos"
                />
              </div>
              <p className="text-xs text-gray-400">
                Si en tu web se puede pedir para recoger, pega aquí el enlace:
                la IA se lo enviará al cliente por WhatsApp en cuanto lo pida.
                Si lo dejas vacío, los pedidos para recoger se seguirán
                pasando a tu equipo por teléfono.
              </p>
            </div>
          )}

          {/* ¿Pedidos por teléfono/WhatsApp con el equipo, o solo agregadores? */}
          {(deliveryEnabled || pickupEnabled) && (
            <>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-amber-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      ¿Recibís pedidos por teléfono o WhatsApp?
                    </p>
                    <p className="text-xs text-gray-500">
                      Desactívalo si SOLO vendéis por Glovo, Uber Eats, etc.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onPhoneOrdersEnabledChange(!phoneOrdersEnabled)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                    phoneOrdersEnabled ? "bg-primary-600" : "bg-gray-200"
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      phoneOrdersEnabled ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>
              <p className="text-xs text-gray-500 pl-4 border-l-2 border-amber-200">
                {phoneOrdersEnabled
                  ? "Activado: los pedidos se transfieren por teléfono a tu equipo. Si no contestáis y el cliente vuelve a llamar, la IA le ofrecerá seguir el pedido por WhatsApp (con WhatsApp IA activo)."
                  : "Desactivado: la IA enviará directamente los enlaces de las apps que tengas configurados (Glovo, Uber Eats, Just Eat) y nunca pasará pedidos a tu equipo."}
              </p>
            </>
          )}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Guardando...
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              Guardar configuración
            </>
          )}
        </button>
      </div>
    </div>
  );
}

/* ==================== STATS TAB ==================== */

function StatsTab({
  stats,
  isOwner,
}: {
  stats: VoiceAiStats | null;
  isOwner: boolean;
}) {
  if (!stats) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto mb-2" />
        <p className="text-sm text-gray-500">Cargando estadísticas...</p>
      </div>
    );
  }

  const intentLabels: Record<string, string> = {
    reservation: "Reservar",
    check_reservation: "Consultar reserva",
    cancel_reservation: "Cancelar reserva",
    info: "Información",
    transfer: "Transferencia",
    other: "Otros",
  };

  const outcomeLabels: Record<string, string> = {
    reservation_created: "Reserva creada",
    reservation_checked: "Reserva consultada",
    reservation_cancelled: "Reserva cancelada",
    info_provided: "Info proporcionada",
    transferred: "Transferida",
    no_action: "Sin acción",
    abandoned: "Abandonada",
    whatsapp_escalated: "Derivada a WhatsApp",
    reservation_modified: "Reserva modificada",
  };

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div
        className={`grid grid-cols-2 ${isOwner ? "md:grid-cols-4" : "md:grid-cols-3"} gap-3`}
      >
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <PhoneCall className="w-4 h-4 text-blue-500" />
            <span className="text-xs font-medium text-gray-500">
              Total llamadas
            </span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.totalCalls}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-purple-500" />
            <span className="text-xs font-medium text-gray-500">
              Minutos totales
            </span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {stats.totalMinutes.toFixed(1)}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-green-500" />
            <span className="text-xs font-medium text-gray-500">
              Reservas creadas
            </span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {stats.byOutcome?.reservation_created || 0}
          </p>
        </div>
        {isOwner && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-amber-500" />
              <span className="text-xs font-medium text-gray-500">
                Coste estimado
              </span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {stats.estimatedCost.toFixed(2)}€
            </p>
          </div>
        )}
      </div>

      {/* Intent Breakdown */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-gray-400" />
          Motivo de las llamadas
        </h3>
        {Object.keys(stats.byIntent).length === 0 ? (
          <p className="text-sm text-gray-400">Aún no hay datos de llamadas.</p>
        ) : (
          <div className="space-y-3">
            {Object.entries(stats.byIntent)
              .sort(([, a], [, b]) => b - a)
              .map(([key, value]) => {
                const total = Object.values(stats.byIntent).reduce(
                  (a, b) => a + b,
                  0,
                );
                const pct = total > 0 ? (value / total) * 100 : 0;
                return (
                  <div key={key}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-700">
                        {intentLabels[key] || key}
                      </span>
                      <span className="text-gray-500">
                        {value} ({pct.toFixed(0)}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div
                        className="bg-primary-500 h-2 rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Outcome Breakdown */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-gray-400" />
          Resultado de las llamadas
        </h3>
        {Object.keys(stats.byOutcome).length === 0 ? (
          <p className="text-sm text-gray-400">Aún no hay datos de llamadas.</p>
        ) : (
          <div className="space-y-3">
            {Object.entries(stats.byOutcome)
              .sort(([, a], [, b]) => b - a)
              .map(([key, value]) => {
                const total = Object.values(stats.byOutcome).reduce(
                  (a, b) => a + b,
                  0,
                );
                const pct = total > 0 ? (value / total) * 100 : 0;
                return (
                  <div key={key}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-700">
                        {outcomeLabels[key] || key}
                      </span>
                      <span className="text-gray-500">
                        {value} ({pct.toFixed(0)}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div
                        className="bg-emerald-500 h-2 rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Monthly */}
      {stats.monthly && stats.monthly.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-gray-400" />
            Evolución mensual
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 text-gray-500 font-medium">
                    Mes
                  </th>
                  <th className="text-right py-2 text-gray-500 font-medium">
                    Llamadas
                  </th>
                  <th className="text-right py-2 text-gray-500 font-medium">
                    Minutos
                  </th>
                </tr>
              </thead>
              <tbody>
                {stats.monthly.map((m) => (
                  <tr key={m.month} className="border-b border-gray-50">
                    <td className="py-2 text-gray-700">{m.month}</td>
                    <td className="py-2 text-right text-gray-700">{m.calls}</td>
                    <td className="py-2 text-right text-gray-700">
                      {m.minutes.toFixed(1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ==================== HISTORY TAB ==================== */

function HistoryTab({
  calls,
  loading,
  page,
  totalPages,
  filter,
  search,
  endedReason,
  isOwner,
  onFilterChange,
  onSearchChange,
  onEndedReasonChange,
  onPageChange,
}: {
  calls: VoiceAiCall[];
  loading: boolean;
  page: number;
  totalPages: number;
  filter: string;
  search: string;
  endedReason: string;
  isOwner: boolean;
  onFilterChange: (v: string) => void;
  onSearchChange: (v: string) => void;
  onEndedReasonChange: (v: string) => void;
  onPageChange: (p: number) => void;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [recordingByCall, setRecordingByCall] = useState<
    Record<string, { status: "loading" | "success" | "error"; url?: string }>
  >({});

  const loadRecording = useCallback((callId: string) => {
    setRecordingByCall((prev) => ({ ...prev, [callId]: { status: "loading" } }));
    apiClient
      .get<{ url: string }>(`/vapi/calls/${callId}/recording`)
      .then((res) => {
        setRecordingByCall((prev) => ({
          ...prev,
          [callId]: { status: "success", url: res.data.url },
        }));
      })
      .catch(() => {
        setRecordingByCall((prev) => ({
          ...prev,
          [callId]: { status: "error" },
        }));
      });
  }, []);

  const outcomeLabels: Record<string, { label: string; color: string }> = {
    reservation_created: {
      label: "Reserva creada",
      color: "bg-green-50 text-green-700 border-green-200",
    },
    info_sent: {
      label: "Info enviada",
      color: "bg-blue-50 text-blue-700 border-blue-200",
    },
    transferred: {
      label: "Transferida",
      color: "bg-amber-50 text-amber-700 border-amber-200",
    },
    abandoned: {
      label: "Abandonada",
      color: "bg-red-50 text-red-700 border-red-200",
    },
    reservation_modified: {
      label: "Reserva modificada",
      color: "bg-sky-50 text-sky-700 border-sky-200",
    },
    whatsapp_escalated: {
      label: "Derivada a WhatsApp",
      color: "bg-emerald-50 text-emerald-700 border-emerald-200",
    },
    general: {
      label: "General",
      color: "bg-gray-50 text-gray-700 border-gray-200",
    },
  };

  const intentLabels: Record<string, string> = {
    reservation: "Reserva",
    check_reservation: "Consultar",
    cancel_reservation: "Cancelar",
    info: "Información",
    transfer: "Transferencia",
    general: "General",
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Buscar por teléfono o transcripción
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Teléfono o palabras clave..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Filtrar por resultado
            </label>
            <select
              value={filter}
              onChange={(e) => onFilterChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            >
              <option value="">Todos los resultados</option>
              <option value="reservation_created">Reserva creada</option>
              <option value="transferred">Transferida</option>
              <option value="info_sent">Info enviada</option>
              <option value="abandoned">Abandonada</option>
              <option value="whatsapp_escalated">Derivada a WhatsApp</option>
              <option value="reservation_modified">Reserva modificada</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Motivo de fin de llamada
            </label>
            <select
              value={endedReason}
              onChange={(e) => onEndedReasonChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            >
              <option value="">Todos los motivos</option>
              <option value="customer-ended-call">Cliente colgó</option>
              <option value="assistant-ended-call">Asistente colgó</option>
              <option value="assistant-said-end-call-phrase">Carmen se despidió</option>
              <option value="assistant-forwarded-call">Transferida al equipo</option>
              <option value="silence-timed-out">Silencio prolongado</option>
              <option value="pipeline-error">Error técnico</option>
              <option value="assistant-error">Error asistente</option>
              <option value="customer-did-not-answer">Sin respuesta</option>
            </select>
          </div>
        </div>
      </div>

      {/* Calls List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Cargando historial...</p>
          </div>
        ) : calls.length === 0 ? (
          <div className="p-8 text-center">
            <Phone className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No hay llamadas registradas</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-200">
              {calls.map((call) => {
                const isExpanded = expandedId === call.id;
                const outcome = outcomeLabels[call.outcome || "general"];
                const durationStr = call.duration
                  ? `${Math.floor(call.duration / 60)}:${String(call.duration % 60).padStart(2, "0")}`
                  : "—";

                return (
                  <div
                    key={call.id}
                    className="p-4 md:p-5 hover:bg-gray-50 transition-colors"
                  >
                    {/* Main Row */}
                    <div
                      className="flex items-center justify-between gap-3 cursor-pointer"
                      onClick={() => {
                        const nextId = isExpanded ? null : call.id;
                        setExpandedId(nextId);
                        if (
                          nextId &&
                          call.recordingUrl &&
                          !recordingByCall[call.id]
                        ) {
                          loadRecording(call.id);
                        }
                      }}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-sm font-medium text-gray-900 truncate">
                            {call.customerName || "Desconocido"}
                          </span>
                          <span className="flex items-center gap-1.5 flex-shrink-0 text-sm font-medium text-gray-900">
                            {durationStr}
                            <ChevronDown
                              className={`w-4 h-4 text-gray-400 transition-transform ${
                                isExpanded ? "rotate-180" : ""
                              }`}
                            />
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 mb-2 truncate">
                          {call.callerPhone || "—"} ·{" "}
                          {new Date(call.createdAt).toLocaleString("es-ES", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {call.intent && (
                            <span className="inline-flex items-center whitespace-nowrap px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                              {intentLabels[call.intent] || call.intent}
                            </span>
                          )}
                          <span
                            className={`inline-flex items-center whitespace-nowrap px-2 py-1 rounded-full text-xs font-medium border ${outcome.color}`}
                          >
                            {outcome.label}
                          </span>
                          {call.endedReason && (
                            <span
                              className={`inline-flex items-center whitespace-nowrap px-2 py-1 rounded-full text-xs font-medium border ${
                                call.endedReason === "silence-timed-out" ||
                                call.endedReason.includes("error")
                                  ? "bg-red-50 text-red-700 border-red-200"
                                  : "bg-gray-50 text-gray-600 border-gray-200"
                              }`}
                              title="Motivo fin de llamada"
                            >
                              {call.endedReason === "customer-ended-call"
                                ? "Cliente colgó"
                                : call.endedReason === "assistant-ended-call"
                                  ? "Asistente colgó"
                                  : call.endedReason === "assistant-said-end-call-phrase"
                                  ? "Carmen se despidió"
                                  : call.endedReason === "assistant-forwarded-call"
                                  ? "Transferida al equipo"
                                  : call.endedReason === "silence-timed-out"
                                    ? "Silencio"
                                    : call.endedReason.includes("error")
                                      ? "Error"
                                      : call.endedReason}
                            </span>
                          )}
                          {call.successEvaluation === "true" && (
                            <span className="inline-flex items-center whitespace-nowrap px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                              ✓ Éxito
                            </span>
                          )}
                          {call.successEvaluation === "false" && (
                            <span className="inline-flex items-center whitespace-nowrap px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                              ✗ Fallida
                            </span>
                          )}
                          {call.reservationId && (
                            <span className="inline-flex items-center whitespace-nowrap px-2 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                              ✓ Reserva #{call.reservationId.substring(0, 8)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Transcript */}
                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                        {call.recordingUrl && (
                          <div className="bg-gray-50 rounded-lg p-3">
                            <h4 className="text-xs font-semibold text-gray-700 mb-2">
                              Grabación:
                            </h4>
                            {(() => {
                              const recording = recordingByCall[call.id];
                              if (!recording || recording.status === "loading") {
                                return (
                                  <p className="text-sm text-gray-500 flex items-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Cargando grabación…
                                  </p>
                                );
                              }
                              if (
                                recording.status === "error" ||
                                !recording.url
                              ) {
                                return (
                                  <p className="text-sm text-red-600">
                                    La grabación no está disponible
                                  </p>
                                );
                              }
                              return (
                                <audio
                                  controls
                                  src={recording.url}
                                  className="w-full"
                                  preload="none"
                                />
                              );
                            })()}
                          </div>
                        )}
                        {call.summary && (
                          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                            <h4 className="text-xs font-semibold text-blue-900 mb-1">
                              Resumen IA:
                            </h4>
                            <p className="text-sm text-blue-900 whitespace-pre-wrap break-words">
                              {call.summary}
                            </p>
                          </div>
                        )}
                        {call.failureReason && (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                            <h4 className="text-xs font-semibold text-amber-900 mb-1">
                              Diagnóstico de fallo:
                            </h4>
                            <p className="text-sm text-amber-900">
                              {call.failureReason}
                            </p>
                          </div>
                        )}
                        {call.transcript && (
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="text-xs font-semibold text-gray-700 mb-2">
                              Transcripción:
                            </h4>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                              {call.transcript}
                            </p>
                          </div>
                        )}
                        {isOwner &&
                          typeof call.cost === "number" &&
                          call.cost > 0 && (
                            <p className="text-xs text-gray-500">
                              Coste estimado: ${call.cost.toFixed(4)}
                            </p>
                          )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
                <div className="text-xs text-gray-500">
                  Página {page} de {totalPages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => onPageChange(page - 1)}
                    disabled={page === 1}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    ← Anterior
                  </button>
                  <button
                    onClick={() => onPageChange(page + 1)}
                    disabled={page === totalPages}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    Siguiente →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
